import React, { useEffect, useState } from "react";
import axios from "axios";
import { LogOut, Image as ImageIcon, Inbox, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import AdminLogin from "./AdminLogin";
import PhotosManager from "./PhotosManager";
import LeadsManager from "./LeadsManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminApp() {
  const [token, setToken] = useState(localStorage.getItem("lc_admin_token") || "");
  const [email, setEmail] = useState(localStorage.getItem("lc_admin_email") || "");
  const [tab, setTab] = useState("photos");
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setChecking(false))
      .catch(() => { logout(); setChecking(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const logout = () => {
    localStorage.removeItem("lc_admin_token");
    localStorage.removeItem("lc_admin_email");
    setToken("");
    setEmail("");
    toast.success("Signed out");
  };

  if (!token) {
    return <AdminLogin onLogin={(d) => { setToken(d.token); setEmail(d.admin.email); }} />;
  }
  if (checking) {
    return <div className="min-h-screen bg-dark text-white grid place-items-center">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-dark text-white noise" data-testid="admin-dashboard">
      <header className="border-b border-white/10 sticky top-0 bg-dark/95 backdrop-blur z-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-lime text-dark grid place-items-center font-display font-bold">L</div>
            <div>
              <div className="font-display text-base leading-tight">Last Cut Admin</div>
              <div className="text-[10px] uppercase tracking-wider text-white/50">{email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-lime px-3 py-2" data-testid="view-site">
              <ExternalLink size={14}/> View site
            </a>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm bg-white/10 hover:bg-white/15 px-3 py-2 rounded-lg">
              <LogOut size={14}/> Sign out
            </button>
          </div>
        </div>
        <nav className="max-w-7xl mx-auto px-5 lg:px-8 flex gap-1 border-t border-white/5">
          <TabBtn active={tab === "photos"} onClick={() => setTab("photos")} icon={<ImageIcon size={14}/>} label="Photos" testid="tab-photos" />
          <TabBtn active={tab === "leads"} onClick={() => setTab("leads")} icon={<Inbox size={14}/>} label="Leads" testid="tab-leads" />
        </nav>
      </header>

      <main className="max-w-7xl mx-auto px-5 lg:px-8 py-8 lg:py-12">
        {tab === "photos" && <PhotosManager token={token} />}
        {tab === "leads" && <LeadsManager token={token} />}
      </main>
    </div>
  );
}

const TabBtn = ({ active, onClick, icon, label, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${active ? "border-lime text-lime" : "border-transparent text-white/60 hover:text-white"}`}
  >
    {icon} {label}
  </button>
);
